import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { DraftId, errors, paxRoute, type RouteAuth, serviceRefusals, UserId } from "../../common";
import { DraftPage, DraftTeam, PresetPermission } from "../../presets/v1alpha1";

const draftStorageAuth: RouteAuth = {
  kind: "service",
  callers: { "preset-server": "drafts:storage" },
};

const DraftParams = z.strictObject({ id: DraftId });

export const EditorAdmission = z.discriminatedUnion("status", [
  z.strictObject({
    status: z.literal("admitted"),
    userId: UserId,
    name: z.string().min(1).meta({ description: "Their display name, for everyone in the draft." }),
    permissions: z.array(PresetPermission),
    team: DraftTeam.meta({
      description: "The team as it is now, which says what they may do with each item.",
    }),
  }),
  z
    .strictObject({ status: z.literal("signedOut") })
    .meta({ description: "The cookie carries no session." }),
  z.strictObject({ status: z.literal("banned") }).meta({ description: "The account is banned." }),
  z
    .strictObject({ status: z.literal("notOnTeam") })
    .meta({ description: "The account isn't the owner, or a member who has joined." }),
  z
    .strictObject({ status: z.literal("noDraft") })
    .meta({ description: "The draft doesn't exist." }),
]);
export type EditorAdmission = z.infer<typeof EditorAdmission>;

const WriteDraftPageResponse = z.strictObject({
  written: z.boolean().meta({ description: "Whether the draft still exists." }),
});

export const admitEditor = oc
  .meta(
    paxRoute({ auth: draftStorageAuth, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/internal/drafts/v1alpha1/{id}/admission",
      operationId: "internal.drafts.admission",
      summary: "Check whether a signed-in account may open a draft.",
      description:
        "Reads the account from the browser's sign-in cookie, which the preset server passes on, checks that it is not banned and is on the draft's preset's team, and records that the draft was opened.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...serviceRefusals })
  .input(
    z.object({
      params: DraftParams,
      headers: z.object({ "x-pax-cookie": z.string().optional() }),
    }),
  )
  .output(EditorAdmission);

export const writeDraftPage = oc
  .meta(
    paxRoute({ auth: draftStorageAuth, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/internal/drafts/v1alpha1/{id}/page",
      operationId: "internal.drafts.page.set",
      summary: "Write a draft's page through to its row.",
      description:
        "The draft session puts its `page:main` record here as it changes, so listings and the version menu read what its team typed.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...serviceRefusals })
  .input(z.object({ params: DraftParams, body: DraftPage }))
  .output(WriteDraftPageResponse);
